import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HelpCircle, Book, MessageCircle, X, ChevronRight, Mail, Zap, Scan } from "lucide-react";
import { Button } from "@/components/ui/button";

export const HelpFloatingButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show on guide page itself
  if (location.pathname === '/guide' || location.pathname === '/user-guide') {
    return null;
  }

  const helpOptions = [
    {
      icon: <Book className="w-4 h-4" />,
      label: "User Guide",
      description: "Step-by-step documentation",
      action: () => navigate('/guide'),
      color: "text-[#00f0ff]"
    },
    {
      icon: <Scan className="w-4 h-4" />,
      label: "Scan Your IaC",
      description: "Upload & scan your code",
      action: () => navigate('/scan'),
      color: "text-emerald-400"
    },
    {
      icon: <Zap className="w-4 h-4" />,
      label: "Quick Start",
      description: "Create your first project",
      action: () => navigate('/wizard'),
      color: "text-green-400"
    },
    {
      icon: <MessageCircle className="w-4 h-4" />,
      label: "FAQ",
      description: "Common questions answered",
      action: () => {
        navigate('/guide');
        setTimeout(() => {
          const searchInput = document.querySelector('[data-testid="guide-search-input"]');
          if (searchInput) searchInput.focus();
        }, 500);
      },
      color: "text-purple-400"
    },
    {
      icon: <Mail className="w-4 h-4" />,
      label: "Contact Support",
      description: "Get help from our team",
      action: () => window.open('mailto:support@zerozone.ai', '_blank'),
      color: "text-yellow-400"
    }
  ];

  return (
    <div className="fixed bottom-20 right-6 z-50" data-testid="help-floating-container">
      {/* Expanded Help Menu */}
      {isOpen && (
        <div 
          className="absolute bottom-16 right-0 w-72 bg-zinc-900/95 backdrop-blur-lg border border-zinc-700/50 rounded-lg shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
          data-testid="help-menu"
        >
          <div className="p-4 border-b border-zinc-700/50">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-[#00f0ff]" />
              Need Help?
            </h3>
            <p className="text-zinc-500 text-xs mt-1">Choose an option below</p>
          </div>
          
          <div className="p-2">
            {helpOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => {
                  option.action();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-800/50 transition-colors group text-left"
                data-testid={`help-option-${index}`}
              >
                <div className={`w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center ${option.color} group-hover:scale-110 transition-transform`}>
                  {option.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{option.label}</p>
                  <p className="text-zinc-500 text-xs truncate">{option.description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>
          
          <div className="p-3 bg-zinc-800/30 border-t border-zinc-700/50">
            <p className="text-zinc-500 text-xs text-center">
              Press <kbd className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-300 text-[10px] font-mono">?</kbd> for keyboard shortcuts
            </p>
          </div>
        </div>
      )}

      {/* Main Help Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg transition-all duration-300 ${
          isOpen 
            ? 'bg-zinc-700 hover:bg-zinc-600 rotate-0' 
            : 'bg-[#00f0ff] hover:bg-[#00f0ff]/90 hover:scale-110'
        }`}
        data-testid="help-floating-btn"
      >
        {isOpen ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <HelpCircle className="w-6 h-6 text-black" />
        )}
      </Button>

      {/* Pulsing indicator for first-time users */}
      {!isOpen && (
        <span className="absolute top-0 right-0 w-3 h-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f0ff] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#00f0ff]"></span>
        </span>
      )}
    </div>
  );
};
